import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  useMemo,
} from "react";
import { Link, useLocation } from "react-router-dom";
import Icon from "@mui/material/Icon";
import { Avatar, Dropdown } from "flowbite-react";
import { permissionChecker, removeToken } from "../utils/permissions";
import { useApplication } from "../contexts/ApplicationContext";
import { calculateExpirationPeriodTest, formatToReal } from "../utils/utils";
import axios from "../config/axiosInstance";
import { useQuery } from "react-query";
import { NEGATIVE, PAYMENTS } from "../config/apiConfig";
import {
  IoIosArrowBack,
  IoIosArrowDown,
  IoIosArrowForward,
  IoMdRefresh,
} from "react-icons/io";
import { MdOutlinePayments } from "react-icons/md";
import ModalAddCredit from "./modals/ModalAddCredit";
import { ModalContract } from "./modals/ModalContract";
import { AnimatePresence, motion } from "framer-motion";
import { createPortal } from "react-dom";
import { Tooltip } from "react-tooltip";

const Sidebar = () => {
  const { user, userPermission, refetchUser } = useApplication();
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const [titlePage, setTitlePage] = useState("Dashboard");
  const sidebarRef = useRef(null);
  const menuBtnRef = useRef(null);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [isOpenModalAddCredit, setIsOpenModalAddCredit] = useState(false);
  const [isOpenModalContract, setIsOpenModalContract] = useState(false);
  const SIDEBAR_COLLAPSED_KEY = "sidebarCollapsed";
  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      return window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "true";
    } catch {
      return false;
    }
  });
  const [hoveredMenu, setHoveredMenu] = useState(null);
  const [hoverRect, setHoverRect] = useState(null);
  const hoverCloseTimeoutRef = useRef(null);
  const [isDesktopView, setIsDesktopView] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth >= 640 : false,
  );
  const COLLAPSED_WIDTH = 75;
  const EXPANDED_WIDTH = 256;
  const isMobileView = !isDesktopView;
  const sidebarWidth = isMobileView
    ? EXPANDED_WIDTH
    : isCollapsed
      ? COLLAPSED_WIDTH
      : EXPANDED_WIDTH;
  const contentMarginLeft = isDesktopView ? sidebarWidth : 0;
  const contentWidth = isDesktopView
    ? `calc(100% - ${sidebarWidth}px)`
    : "100%";
  const isObahCredito =
    typeof window !== "undefined" &&
    window.location.href.includes("obahcredito");
  const isRepresentative = user?.Company?.companyTypeId === "REPRESENTANTE";
  const shouldHideObahMenuItems = isObahCredito && !isRepresentative;
  const obahHiddenMenuItems = new Set([
    "Minhas Faturas",
    "Usuários",
    "Documentação",
    "Suporte",
  ]);
  const clearHoverClose = useCallback(() => {
    if (hoverCloseTimeoutRef.current) {
      clearTimeout(hoverCloseTimeoutRef.current);
      hoverCloseTimeoutRef.current = null;
    }
  }, []);
  const scheduleHoverClose = useCallback(() => {
    clearHoverClose();
    hoverCloseTimeoutRef.current = setTimeout(() => {
      setHoveredMenu(null);
      setHoverRect(null);
    }, 180);
  }, [clearHoverClose]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const widthValue = isDesktopView ? `${sidebarWidth}px` : "0px";
    document.documentElement.style.setProperty("--sidebar-width", widthValue);
  }, [sidebarWidth, isDesktopView]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(
        SIDEBAR_COLLAPSED_KEY,
        isCollapsed ? "true" : "false",
      );
    } catch {
      // ignore write errors
    }
  }, [isCollapsed]);
  const daysLeft = calculateExpirationPeriodTest(
    user?.Company?.planExpirationDate,
  );
  const canSeeInvoices = permissionChecker(["ADMIN"], userPermission, user, [
    "ALL",
  ]);
  const isAdminUser = Array.isArray(userPermission)
    ? userPermission.includes("ADMIN")
    : userPermission === "ADMIN";
  const isPowerUser = Array.isArray(userPermission)
    ? userPermission.includes("POWER_USER")
    : userPermission === "POWER_USER";
  const canSeeNegativesAdmin =
    (isAdminUser || isPowerUser) && Number(user?.Company?.id) === 1;
  const canSeeContract = isAdminUser;
  const coercePaid = (isPaid) =>
    isPaid === true || isPaid === 1 || isPaid === "1";

  const { data: hasBoletoNotification = false } = useQuery(
    ["sidebar-boleto-notification", user?.Company?.id],
    async () => {
      const { data } = await axios.get(
        `${PAYMENTS.GET_ALL}?isPaid=false&itemsPerPage=-1`,
      );
      const rows = data?.data?.data || [];
      return rows.some(
        (inv) => inv?.type === "BOLETO" && !coercePaid(inv?.isPaid),
      );
    },
    {
      enabled: Boolean(canSeeInvoices && user?.Company?.id),
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
    },
  );

  const { data: negativePendingCount = 0 } = useQuery(
    ["sidebar-negative-pending", user?.Company?.id],
    async () => {
      const { data } = await axios.get(NEGATIVE.LIST, {
        params: {
          all: 1,
          status: "ABERTO",
          page: 1,
          pageSize: 1,
        },
      });
      return Number(data?.total) || 0;
    },
    {
      enabled: Boolean(canSeeNegativesAdmin),
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
    },
  );

  const handleToggleSidebar = (linkName = "") => {
    setIsOpen(!isOpen);
    if (linkName !== "") setTitlePage(linkName);
  };

  const toggleCollapse = () => {
    if (!isDesktopView) return;
    setIsCollapsed((prev) => !prev);
  };

  const collapsedTooltip = useMemo(() => {
    if (
      !isDesktopView ||
      !isCollapsed ||
      !hoveredMenu ||
      !hoverRect ||
      typeof document === "undefined"
    ) {
      return null;
    }

    const allowedChildren = (hoveredMenu.children || []).filter((child) =>
      permissionChecker(
        child.permission,
        userPermission,
        user,
        child.typeCompany ?? hoveredMenu.typeCompany,
      ),
    );

    if (!allowedChildren.length) return null;

    const style = {
      position: "fixed",
      top: hoverRect.top,
      left: hoverRect.right + 8,
      width: 220,
      zIndex: 9999,
    };

    return createPortal(
      <div
        className="bg-white/95 text-slate-800 rounded-lg shadow-lg border border-slate-200 pointer-events-auto"
        style={style}
        onMouseEnter={clearHoverClose}
        onMouseLeave={scheduleHoverClose}
      >
        <div className="flex flex-col p-2 gap-1">
          {allowedChildren.map((child, idx) => {
            const isChildActive = location.pathname === child.link;
            const showNegativesPending =
              child.link === "/admin/negatives" && negativePendingCount > 0;
            return (
              <Link
                to={child.link}
                key={idx}
                onClick={() => {
                  handleToggleSidebar(formatTitle(child.name));
                  setHoveredMenu(null);
                }}
                className={`flex items-center gap-2 px-3 py-1 rounded-md text-sm transition-colors ${
                  isChildActive
                    ? "text-green-600 font-semibold"
                    : "text-slate-700 hover:text-slate-900"
                }`}
              >
                {child.icon && <Icon className="text-base">{child.icon}</Icon>}
                <span className="truncate">{child.name}</span>
                {showNegativesPending && (
                  <span className="ml-auto h-4 min-w-[18px] px-1.5 flex items-center justify-center rounded-full bg-rose-500 text-white text-[10px] font-bold">
                    {negativePendingCount}
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      </div>,
      document.body,
    );
  }, [
    isCollapsed,
    hoveredMenu,
    hoverRect,
    userPermission,
    user,
    location.pathname,
    clearHoverClose,
    scheduleHoverClose,
    isDesktopView,
    negativePendingCount,
  ]);

  useEffect(() => {
    if (!isDesktopView && isCollapsed) {
      setIsCollapsed(false);
    }
  }, [isDesktopView, isCollapsed]);

  const toggleMenu = (menuName) => {
    setExpandedMenus((prev) => {
      const isAlreadyExpanded = prev[menuName];
      if (isAlreadyExpanded) return {};
      return { [menuName]: true };
    });
  };

  const handleRefresh = () => {
    refetchUser();
  };

  /* ===== helper de título ===== */
  const formatTitle = (name = "") => {
    const n = (name || "").toLowerCase();
    if (n.includes("crédito") || n === "créditos")
      return "Consultas de crédito";
    if (n.includes("cadastra") || n === "cadastrais")
      return "Consultas cadastrais";
    if (n.includes("veicular") || n === "veiculares")
      return "Consultas veiculares";
    return name || "Visão Geral";
  };

  const getTitleFromPath = (pathname) => {
    if (pathname.startsWith("/queries/credits")) return "Consultas de crédito";
    if (pathname.startsWith("/queries/cadastrals"))
      return "Consultas cadastrais";
    if (pathname.startsWith("/queries/autos")) return "Consultas veiculares";
    return (
      menuLinks.find((link) => link.link === pathname)?.name || "Visão Geral"
    );
  };

  /* ===== PERFIL ===== */
  const isSeller = Array.isArray(userPermission)
    ? userPermission.includes("SELLER")
    : userPermission === "SELLER";

  /* ===== MENU BASE (não SELLER) ===== */
  const baseMenuLinks = [
    {
      name: "Visão Geral",
      link: "/dashboard",
      icon: "equalizer",
      permission: ["USER", "USER_CALENDARS"],
      isCollapsible: false,
      typeCompany: ["ALL"],
    },
    {
      name: "Consulte aqui",
      link: "/queries",
      icon: "manage_search",
      permission: ["USER"],
      isCollapsible: true,
      typeCompany: ["ALL"],
      children: [
        {
          name: "Motor de crédito",
          link: "/credit-engine",
          icon: "verified_user",
          permission: ["USER"],
          isCollapsible: false,
          typeCompany: ["ALL"],
        },
        {
          name: "Créditos",
          link: "/queries/credits",
          icon: "attach_money",
          permission: ["USER"],
          isCollapsible: false,
          typeCompany: ["ALL"],
        },
        {
          name: "Cadastrais",
          link: "/queries/cadastrals",
          icon: "account_box",
          permission: ["USER"],
          isCollapsible: false,
          typeCompany: ["ALL"],
        },
        {
          name: "Veiculares",
          link: "/queries/autos",
          icon: "time_to_leave",
          permission: ["USER"],
          isCollapsible: false,
          typeCompany: ["ALL"],
        },
      ],
    },
    {
      name: "Localizador PF/PJ",
      link: "/localize",
      icon: "travel_explore",
      permission: ["ADMIN"],
      isCollapsible: false,
      typeCompany: ["ALL"],
    },
    {
      name: "Negative Clientes",
      link: "/negatives",
      icon: "thumb_down_alt",
      permission: ["USER"],
      isCollapsible: false,
      typeCompany: ["ALL"],
    },
    {
      name: "Relatórios",
      link: "/reports",
      icon: "query_stats",
      permission: ["USER"],
      isCollapsible: true,
      typeCompany: ["ALL"],
      children: [
        {
          name: "Consultas",
          link: "/reports/queries",
          icon: "manage_search",
          permission: ["USER"],
          isCollapsible: false,
          typeCompany: ["ALL"],
        },
      ],
    },

    {
      name: "Minhas Faturas",
      link: "/invoices",
      icon: "request_quote",
      permission: ["ADMIN"],
      isCollapsible: false,
      typeCompany: ["ALL"],
    },
    {
      name: "Usuários",
      link: "/users",
      icon: "group",
      permission: ["ADMIN"],
      isCollapsible: false,
      typeCompany: ["ALL"],
    },
    {
      name: "Suporte",
      link: "/support",
      icon: "support_agent",
      permission: ["USER"],
      isCollapsible: false,
      typeCompany: ["ALL"],
    },
    {
      name: "Administrativo",
      link: "/support",
      icon: "settings",
      permission: ["ADMIN"],
      isCollapsible: true,
      typeCompany: ["REPRESENTANTE", "ADMIN"],
      children: [
        {
          name: "Review ⭐",
          link: "/admin/daily-review",
          icon: "stacked_bar_chart",
          permission: ["ADMIN"],
          isCollapsible: false,
          typeCompany: ["REPRESENTANTE", "ADMIN"],
        },
        {
          name: "Relatórios",
          link: "/admin/reports",
          icon: "query_stats",
          permission: ["ADMIN"],
          isCollapsible: false,
          typeCompany: ["REPRESENTANTE", "ADMIN"],
        },
        {
          name: "Negativações",
          link: "/admin/negatives",
          icon: "policy",
          permission: ["POWER_USER"],
          isCollapsible: false,
          typeCompany: ["ADMIN"],
        },
        {
          name: "Financeiro",
          link: "/admin/invoices",
          icon: "receipt",
          permission: ["ADMIN"],
          isCollapsible: false,
          typeCompany: ["REPRESENTANTE", "ADMIN"],
        },
        {
          name: "Clientes",
          link: "/admin/customers",
          icon: "person",
          permission: ["ADMIN"],
          isCollapsible: false,
          typeCompany: ["REPRESENTANTE", "ADMIN"],
        },
        {
          name: "Representantes",
          link: "/admin/representatives",
          icon: "group_2",
          permission: ["ADMIN"],
          isCollapsible: false,
          typeCompany: ["REPRESENTANTE", "ADMIN"],
        },
        {
          name: "Tabela de Custos",
          link: "/admin/table-cost",
          icon: "request_page",
          permission: ["ADMIN"],
          isCollapsible: false,
          typeCompany: ["REPRESENTANTE", "ADMIN"],
        },
        {
          name: "Banners",
          link: "/admin/banners",
          icon: "image",
          permission: ["ADMIN"],
          isCollapsible: false,
          typeCompany: ["REPRESENTANTE", "ADMIN"],
        },
        {
          name: "Configurações",
          link: "/admin/admin",
          icon: "settings_suggest",
          permission: ["ADMIN"],
          isCollapsible: false,
          typeCompany: ["REPRESENTANTE", "ADMIN"],
        },
      ],
    },
  ];

  /* ===== MENU SELLER (sem o "Administrativo") ===== */
  const rawSellerMenu = [
    {
      name: "Dashboard",
      link: "/dashboard",
      icon: "equalizer",
      permission: ["USER", "USER_CALENDARS"],
      isCollapsible: false,
      typeCompany: ["ALL"],
    },
    {
      name: "Consultas",
      link: "/queries",
      icon: "manage_search",
      permission: ["USER"],
      isCollapsible: true,
      typeCompany: ["ALL"],
      children: [
        {
          name: "Créditos",
          link: "/queries/credits",
          icon: "attach_money",
          permission: ["USER"],
          isCollapsible: false,
          typeCompany: ["ALL"],
        },
        {
          name: "Cadastrais",
          link: "/queries/cadastrals",
          icon: "account_box",
          permission: ["USER"],
          isCollapsible: false,
          typeCompany: ["ALL"],
        },
        {
          name: "Veiculares",
          link: "/queries/autos",
          icon: "time_to_leave",
          permission: ["USER"],
          isCollapsible: false,
          typeCompany: ["ALL"],
        },
      ],
    },
    {
      name: "Localizador PF/PJ",
      link: "/localize",
      icon: "travel_explore",
      permission: ["ADMIN"],
      isCollapsible: false,
      typeCompany: ["ALL"],
    },
    {
      name: "Negative Clientes",
      link: "/negatives",
      icon: "thumb_down_alt",
      permission: ["USER"],
      isCollapsible: false,
      typeCompany: ["ALL"],
    },
    {
      name: "Relatórios",
      link: "/reports",
      icon: "query_stats",
      permission: ["USER"],
      isCollapsible: true,
      typeCompany: ["ALL"],
      children: [
        {
          name: "Consultas",
          link: "/reports/queries",
          icon: "manage_search",
          permission: ["USER"],
          isCollapsible: false,
          typeCompany: ["ALL"],
        },
      ],
    },

    // ✅ novo item só para SELLER
    {
      name: "Meus Clientes",
      link: "/my-customers",
      icon: "groups_3",
      permission: ["SELLER"],
      isCollapsible: false,
      typeCompany: ["ALL"],
    },

    {
      name: "Dúvidas Frequentes",
      link: "/faq",
      icon: "help_outline",
      permission: ["USER"],
      isCollapsible: false,
      typeCompany: ["ALL"],
    },
    {
      name: "Suporte",
      link: "/support",
      icon: "support_agent",
      permission: ["USER"],
      isCollapsible: false,
      typeCompany: ["ALL"],
    },
  ];

  /* === Dá permissão SELLER para todos os itens/filhos do menu SELLER === */
  const addSellerPermission = (item) => {
    const patched = {
      ...item,
      permission: Array.from(new Set([...(item.permission || []), "SELLER"])),
      typeCompany:
        item.typeCompany && item.typeCompany.length
          ? item.typeCompany
          : ["ALL"],
    };
    if (item.children && item.children.length) {
      patched.children = item.children.map(addSellerPermission);
    }
    return patched;
  };

  const sellerMenuLinks = useMemo(
    () => rawSellerMenu.map(addSellerPermission),
    [],
  );

  /* ===== Escolha final conforme perfil ===== */
  const menuLinks = isSeller ? sellerMenuLinks : baseMenuLinks;

  const closeSidebar = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleResize = () => setIsDesktopView(window.innerWidth >= 640);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    return () => {
      if (hoverCloseTimeoutRef.current) {
        clearTimeout(hoverCloseTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target) &&
        !menuBtnRef.current.contains(event.target) &&
        isOpen
      ) {
        closeSidebar();
      }
    };

    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [location.pathname, isOpen]);

  useEffect(() => {
    setTitlePage(formatTitle(getTitleFromPath(location.pathname)));
  }, [location.pathname, menuLinks]);

  let logoSrc;

  switch (true) {
    case window.location.href.includes("avaliecreditoconsultas"):
      logoSrc = "/assets/white-label/avalie-credito/logo_branco.png";
      break;
    case window.location.href.includes("acessopositivo"):
      logoSrc = "/assets/white-label/acesso-positivo/logo_acesso.png";
      break;
    case window.location.href.includes("proconsultbr"):
      logoSrc = "/assets/white-label/pro-consult-br/logo_branco.png";
      break;
    case window.location.href.includes("simscore"):
      logoSrc = "/assets/white-label/sim-pesquisa/logo_branco.png";
      break;
    case window.location.href.includes("simpesquisa"):
      logoSrc = "/assets/white-label/sim-pesquisa/logo_branco.png";
      break;
    case window.location.href.includes("credoresbrasil"):
      logoSrc = "/assets/white-label/credores-brasil/logo_branco.png";
      break;
    case window.location.href.includes("consultaspositivo"):
      logoSrc = "/assets/white-label/consultas-positivo/logo_branco.png";
      break;
    case window.location.href.includes("raioxconsultas"):
      logoSrc = "/assets/white-label/raio-x/logo_branco.png";
      break;
    case window.location.href.includes("obahcredito"):
      logoSrc = "/assets/white-label/obah-credito/logo_branco.png";
      break;
    case window.location.href.includes("hellusconsultoria"):
      logoSrc = "/assets/white-label/hellus-consultoria/logo_branco.png";
      break;
    case window.location.href.includes("nomenoazul"):
      logoSrc = "/assets/white-label/nome-no-azul/logo_branco.png";
      break;
    case window.location.href.includes("finposcore"):
      logoSrc = "/assets/white-label/finpo-score/logo_branco.png";
      break;
    case window.location.href.includes("locagora"):
      logoSrc = "/assets/white-label/locagora/logo_branco.png";
      break;
    case window.location.href.includes("riscocred"):
      logoSrc = "/assets/white-label/riscocred/logo_branco.png";
      break;
    case window.location.href.includes("ampliacapital"):
      logoSrc = "/assets/white-label/amplia-capital/logo_branco.png";
      break;
    default:
      logoSrc = "/assets/logo-branco.png";
      break;
  }

  return (
    <>
      {/* ── Sidebar ── */}
      <aside
        id="logo-sidebar"
        ref={sidebarRef}
        className={`fixed top-0 left-0 z-40 h-screen sm:translate-x-0 ${
          isOpen ? "" : "-translate-x-full"
        }`}
        aria-label="Sidebar"
        style={{
          width: `${sidebarWidth}px`,
          transition: "width 0.25s cubic-bezier(0.4,0,0.2,1)",
        }}
      >
        <div className="h-full flex flex-col">
          {/* ── Logo ── */}
          <div
            className={`flex flex-col items-center ${isCollapsed ? "pt-5 pb-4" : "pt-6 pb-3 px-4"}`}
          >
            <img
              src={logoSrc}
              alt="Logo"
              className="object-contain mx-auto"
              style={{
                height: isCollapsed ? "22px" : "46px",
                transition: "height 0.25s ease",
              }}
            />
            <button
              onClick={toggleCollapse}
              className={`hidden sm:flex mt-3 items-center justify-center transition-all rounded-lg ${
                isCollapsed
                  ? "w-8 h-8 bg-white/5 hover:bg-white/10 text-white/40 hover:text-white/60"
                  : "gap-1.5 px-3 py-1 text-[10px] font-semibold text-white/25 hover:text-white/50 tracking-widest uppercase"
              }`}
            >
              {isCollapsed ? (
                <IoIosArrowForward size={13} />
              ) : (
                <>
                  <IoIosArrowBack size={11} />
                  <span>Minimizar</span>
                </>
              )}
            </button>
          </div>

          {/* ── Separator ── */}
          <div className="mx-4 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

          {/* ── Credit balance (PRE) ── */}
          {user?.Company?.typePayment === "PRE" && (
            <div
              className={`mx-3 mt-3 mb-1 ${isCollapsed ? "flex flex-col items-center gap-2" : ""}`}
            >
              {!isCollapsed ? (
                <div
                  className="rounded-xl border border-white/10 px-3 py-2.5"
                  style={{ background: "rgba(255,255,255,0.04)" }}
                >
                  <p className="text-[10px] font-semibold text-white/30 uppercase tracking-widest mb-1.5">
                    Saldo disponível
                  </p>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-bold text-white">
                      {formatToReal(user?.Company?.credit || 0)}
                    </span>
                    <div className="flex gap-1.5">
                      <button
                        data-tooltip-id="tooltip-add-credit"
                        onClick={() => setIsOpenModalAddCredit(true)}
                        className="w-7 h-7 rounded-lg flex items-center justify-center transition hover:scale-105"
                        style={{ background: "rgba(59,130,246,0.2)" }}
                      >
                        <MdOutlinePayments
                          className="text-blue-300"
                          size={14}
                        />
                      </button>
                      <button
                        data-tooltip-id="tooltip-refresh-credit"
                        onClick={handleRefresh}
                        className="w-7 h-7 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center transition"
                      >
                        <IoMdRefresh className="text-white/45" size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <button
                  data-tooltip-id="tooltip-add-credit"
                  onClick={() => setIsOpenModalAddCredit(true)}
                  className="w-9 h-9 rounded-xl flex items-center justify-center transition hover:scale-105"
                  style={{ background: "rgba(59,130,246,0.2)" }}
                >
                  <MdOutlinePayments className="text-blue-300" size={16} />
                </button>
              )}
              <Tooltip id="tooltip-add-credit" content="Adicionar crédito" />
              <Tooltip id="tooltip-refresh-credit" content="Atualizar saldo" />
            </div>
          )}

          {/* ── Navigation ── */}
          <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar py-3 px-2">
            {daysLeft > 0 && (
              <nav className="flex flex-col gap-0.5">
                {menuLinks.map((menuLink, index) => {
                  const isActive = location.pathname === menuLink.link;
                  const isExpanded = expandedMenus[menuLink.name];
                  const hasChildren =
                    Array.isArray(menuLink.children) &&
                    menuLink.children.length > 0;
                  const hasPermission = permissionChecker(
                    menuLink.permission,
                    userPermission,
                    user,
                    menuLink.typeCompany,
                  );
                  const showBoletoNotification =
                    menuLink.name === "Minhas Faturas" && hasBoletoNotification;
                  const isNegativesAdminItem =
                    menuLink.link === "/admin/negatives";
                  const isChildActive = hasChildren
                    ? (menuLink.children || []).some(
                        (c) => location.pathname === c.link,
                      )
                    : false;
                  const itemActive = isActive || isChildActive;

                  if (
                    shouldHideObahMenuItems &&
                    obahHiddenMenuItems.has(menuLink.name)
                  )
                    return null;
                  if (isNegativesAdminItem && !canSeeNegativesAdmin)
                    return null;
                  if (!hasPermission) return null;

                  return (
                    <div key={index}>
                      {!menuLink.isCollapsible ? (
                        <Link
                          to={menuLink.link}
                          onClick={() =>
                            handleToggleSidebar(formatTitle(menuLink.name))
                          }
                          title={isCollapsed ? menuLink.name : undefined}
                          className={`relative group flex items-center rounded-xl py-2.5 transition-all duration-150 ${
                            isCollapsed ? "justify-center px-2" : "gap-3 px-3"
                          } ${
                            isActive
                              ? "text-white"
                              : "text-white/45 hover:text-white/80 hover:bg-white/5"
                          } ${showBoletoNotification ? "relative" : ""}`}
                          style={
                            isActive
                              ? { background: "rgba(59,130,246,0.12)" }
                              : {}
                          }
                        >
                          {isActive && !isCollapsed && (
                            <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-r-full bg-blue-400" />
                          )}
                          <Icon
                            className={`text-[18px] shrink-0 transition-colors ${
                              isActive
                                ? "text-blue-400"
                                : "text-white/35 group-hover:text-white/65"
                            }`}
                          >
                            {menuLink.icon}
                          </Icon>
                          {!isCollapsed && (
                            <span className="text-[13px] font-medium leading-none flex-1 truncate">
                              {menuLink.name}
                            </span>
                          )}
                          {showBoletoNotification && (
                            <span
                              className={`inline-flex ${
                                isCollapsed
                                  ? "absolute top-1.5 right-1"
                                  : "ml-auto"
                              }`}
                            >
                              <span className="absolute inline-flex h-2 w-2 rounded-full bg-rose-400 opacity-75 animate-ping" />
                              <span className="relative inline-flex h-2 w-2 rounded-full bg-rose-500" />
                            </span>
                          )}
                        </Link>
                      ) : (
                        <div
                          className="relative"
                          onMouseEnter={(event) => {
                            if (!isCollapsed) return;
                            clearHoverClose();
                            setHoveredMenu(menuLink);
                            setHoverRect(
                              event.currentTarget.getBoundingClientRect(),
                            );
                          }}
                          onMouseMove={(event) => {
                            if (!isCollapsed) return;
                            setHoverRect(
                              event.currentTarget.getBoundingClientRect(),
                            );
                          }}
                          onMouseLeave={() => {
                            if (!isCollapsed) return;
                            scheduleHoverClose();
                          }}
                        >
                          <button
                            onClick={() => toggleMenu(menuLink.name)}
                            title={isCollapsed ? menuLink.name : undefined}
                            className={`relative group flex items-center w-full rounded-xl py-2.5 transition-all duration-150 ${
                              isCollapsed
                                ? "justify-center px-2"
                                : "gap-3 px-3 justify-between"
                            } ${
                              itemActive
                                ? "text-white"
                                : "text-white/45 hover:text-white/80 hover:bg-white/5"
                            }`}
                            style={
                              itemActive
                                ? { background: "rgba(59,130,246,0.12)" }
                                : {}
                            }
                          >
                            {itemActive && !isCollapsed && (
                              <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-r-full bg-blue-400" />
                            )}
                            <div
                              className={`flex items-center ${isCollapsed ? "" : "gap-3"}`}
                            >
                              <Icon
                                className={`text-[18px] shrink-0 transition-colors ${
                                  itemActive
                                    ? "text-blue-400"
                                    : "text-white/35 group-hover:text-white/65"
                                }`}
                              >
                                {menuLink.icon}
                              </Icon>
                              {!isCollapsed && (
                                <span className="text-[13px] font-medium leading-none">
                                  {menuLink.name}
                                </span>
                              )}
                            </div>
                            {!isCollapsed && (
                              <IoIosArrowDown
                                className={`text-white/25 transition-transform duration-200 shrink-0 ${
                                  isExpanded ? "rotate-180" : ""
                                }`}
                                size={13}
                              />
                            )}
                          </button>

                          <AnimatePresence initial={false}>
                            {isExpanded && !isCollapsed && (
                              <motion.div
                                key="submenu"
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{
                                  duration: 0.2,
                                  ease: "easeInOut",
                                }}
                                className="overflow-hidden"
                              >
                                <div className="ml-4 pl-3 border-l border-white/10 mt-1 mb-2 flex flex-col gap-0.5">
                                  {(menuLink.children || [])
                                    .filter((child) =>
                                      permissionChecker(
                                        child.permission,
                                        userPermission,
                                        user,
                                        child.typeCompany ??
                                          menuLink.typeCompany,
                                      ),
                                    )
                                    .map((child, subIndex) => {
                                      const isChildItemActive =
                                        location.pathname === child.link;
                                      const showNegativesPending =
                                        child.link === "/admin/negatives" &&
                                        negativePendingCount > 0;
                                      return (
                                        <Link
                                          to={child.link}
                                          key={subIndex}
                                          onClick={() =>
                                            handleToggleSidebar(
                                              formatTitle(child.name),
                                            )
                                          }
                                          className={`group flex items-center gap-2 rounded-lg px-2.5 py-[7px] text-[12px] transition-all duration-150 ${
                                            isChildItemActive
                                              ? "text-white font-semibold"
                                              : "text-gray-300 hover:text-white/70 hover:bg-white/5"
                                          }`}
                                          style={
                                            isChildItemActive
                                              ? {
                                                  background:
                                                    "rgba(59,130,246,0.10)",
                                                }
                                              : {}
                                          }
                                        >
                                          {child.icon && (
                                            <Icon
                                              className={`text-[15px] shrink-0 ${
                                                isChildItemActive
                                                  ? "text-blue-300"
                                                  : "text-white/25 group-hover:text-white/50"
                                              }`}
                                            >
                                              {child.icon}
                                            </Icon>
                                          )}
                                          <span className="truncate flex-1">
                                            {child.name}
                                          </span>
                                          {showNegativesPending && (
                                            <span className="ml-auto h-4 min-w-[18px] px-1.5 flex items-center justify-center rounded-full bg-rose-500 text-white text-[10px] font-bold">
                                              {negativePendingCount}
                                            </span>
                                          )}
                                        </Link>
                                      );
                                    })}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      )}
                    </div>
                  );
                })}
              </nav>
            )}
          </div>

          {/* ── User profile (bottom) ── */}
          <div className="mt-auto">
            <div className="mx-4 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent mb-3" />
            <div
              className={`px-3 pb-5 ${isCollapsed ? "flex justify-center" : ""}`}
            >
              {isCollapsed ? (
                <Avatar rounded size="sm" img={user?.imageUrl} />
              ) : (
                <div className="flex items-center gap-2.5 px-1">
                  <Avatar rounded size="sm" img={user?.imageUrl} />
                  <div className="min-w-0 flex-1">
                    <p className="text-[12px] font-semibold text-white/80 truncate leading-none mb-0.5">
                      {user?.name}
                    </p>
                    <p className="text-[10px] text-white/30 truncate leading-none">
                      {user?.email}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </aside>

      {/* ── Top bar ── */}
      <div
        className="mt-0 transition-all duration-300"
        style={{ marginLeft: `${contentMarginLeft}px`, width: contentWidth }}
      >
        <div className="container mx-auto">
          <div
            className="mainbar px-5"
            style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}
          >
            <div className="flex justify-between items-center h-full">
              <div className="flex items-center gap-3">
                <button
                  ref={menuBtnRef}
                  type="button"
                  onClick={() => handleToggleSidebar()}
                  className="inline-flex items-center p-2 text-white/50 hover:text-white rounded-lg sm:hidden focus:outline-none transition"
                >
                  <span className="sr-only">Abrir sidebar</span>
                  <svg
                    className="w-5 h-5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      clipRule="evenodd"
                      fillRule="evenodd"
                      d="M2 4.75A.75.75 0 012.75 4h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 4.75zm0 10.5a.75.75 0 01.75-.75h7.5a.75.75 0 010 1.5h-7.5a.75.75 0 01-.75-.75zM2 10a.75.75 0 01.75-.75h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 10z"
                    />
                  </svg>
                </button>
                <h2 className="font-semibold text-white text-[15px] tracking-tight">
                  {titlePage}
                </h2>
              </div>

              <div className="flex items-center gap-3">
                <span className="hidden md:block text-sm text-white/40">
                  Olá,{" "}
                  <span className="text-white/65 font-medium">
                    {user?.name?.split(" ")[0]}
                  </span>
                </span>
                <Dropdown
                  label={<Avatar rounded size="sm" img={user?.imageUrl} />}
                  arrowIcon={false}
                  inline
                  className="z-50"
                >
                  <Dropdown.Header>
                    <span className="block truncate text-sm font-medium">
                      {user?.email}
                    </span>
                  </Dropdown.Header>
                  <Dropdown.Item className="py-0 px-0">
                    <Link
                      to="/profile"
                      className="w-full py-2 text-left px-4"
                      onClick={() => setTitlePage("Meu Perfil")}
                    >
                      Meu perfil
                    </Link>
                  </Dropdown.Item>
                  {canSeeContract && (
                    <Dropdown.Item className="py-0 px-0">
                      <button
                        type="button"
                        className="w-full py-2 text-left px-4"
                        onClick={() => setIsOpenModalContract(true)}
                      >
                        Meu contrato
                      </button>
                    </Dropdown.Item>
                  )}
                  <Dropdown.Divider />
                  <Dropdown.Item className="py-0 px-0">
                    <Link
                      to="/lgpd"
                      className="w-full py-2 text-left px-4"
                      onClick={() => setTitlePage("Aviso LGPD")}
                    >
                      Aviso LGDP
                    </Link>
                  </Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Item onClick={removeToken}>Sair</Dropdown.Item>
                </Dropdown>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ModalAddCredit
        isOpen={isOpenModalAddCredit}
        setOpenModal={setIsOpenModalAddCredit}
      />
      <ModalContract
        isOpen={isOpenModalContract}
        onClose={() => setIsOpenModalContract(false)}
      />
      {collapsedTooltip}
    </>
  );
};

export default Sidebar;
